import { cloneElement, isValidElement } from 'react';
import { Button } from '@aimsos/ui';

/**
 * Modal footer: error + Cancel + primary action.
 *
 * Duplicated from
 * `aims-os-governance/src/domains/truth-plane/components/Modals/parts/ModalFooter.jsx`
 * (CT-5183 "Duplicate locally"). It already handles the `submitting` /
 * `submitError` / `submitDisabled` contract and clones the primary button with
 * `loading` / `disabled`, so no dialog in this domain hand-rolls a footer.
 * @param {object} props
 * @param {string} props.cancelLabel
 * @param {() => void} props.onCancel
 * @param {import('react').ReactNode} props.primaryAction — typically `<Button …>`
 * @param {boolean} [props.submitting]
 * @param {string} [props.submitError]
 * @param {import('react').ReactElement} [props.secondaryAction] — **un solo botón**
 *   (se clona con `disabled` mientras `submitting`; un Fragment o un wrapper no
 *   lo recibiría). Botón extra a la
 *   izquierda del primario. No se clona con `loading`: solo el primario
 *   muestra el spinner. Sí queda `disabled` mientras `submitting`.
 *
 * While `submitting`, **every** action in the footer is disabled, not only the
 * primary: Cancel and the secondary one used to stay clickable mid-request —
 * they looked available and did nothing, or closed the dialog under a save
 * still running (CT-5504). One rule here covers every dialog that uses it.
 * @param {boolean} [props.submitDisabled]
 * @param {string} [props.statusText] — neutral note on the left (e.g. "Select an
 *   entity to continue"). Shown only when there is no `submitError`: an
 *   informational state must not inherit an error's red or its `role="alert"`.
 * @param {string} [props.className] — e.g. padding
 */
const ModalFooter = ({
  cancelLabel,
  onCancel,
  primaryAction,
  secondaryAction,
  submitting = false,
  submitError,
  submitDisabled = false,
  statusText,
  className = 'px-6 py-4',
}) => {
  const primary =
    isValidElement(primaryAction) && (submitting || submitDisabled)
      ? cloneElement(primaryAction, {
          loading: submitting || primaryAction.props?.loading,
          disabled: submitDisabled || submitting || primaryAction.props?.disabled,
        })
      : primaryAction;

  const secondary =
    isValidElement(secondaryAction) && submitting
      ? cloneElement(secondaryAction, { disabled: true })
      : secondaryAction;

  return (
    <div className={['shrink-0 border-t border-edge', className].filter(Boolean).join(' ')}>
      <div className="flex w-full items-center gap-2.5">
        {submitError ? (
          <p className="min-w-0 flex-1 pr-2 text-left text-[11px] leading-snug text-red-400" role="alert">
            {submitError}
          </p>
        ) : statusText ? (
          <p className="min-w-0 flex-1 pr-2 text-left text-[11px] leading-snug text-content-muted">
            {statusText}
          </p>
        ) : (
          <div className="flex-1" aria-hidden />
        )}
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={submitting}
          className="!text-[12px]"
        >
          {cancelLabel}
        </Button>
        {secondary}
        {primary}
      </div>
    </div>
  );
};

export default ModalFooter;
